<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <NavigationPhotos
    :total-photos="photos.total"
    :years="photos.years"
    :photo-data-by-year="photos.byYear"
    @setYear="setYear" />
  <aside>
    To share VIZBI photos, upload them to
    <a href="https://www.flickr.com/signin">Flickr</a>
    &amp; tag with &lsquo;<a href="https://www.flickr.com/search/?tags=vizbi&sort=date-taken&view_all=1">vizbi</a
    >&rsquo;.
  </aside>
  <div id="wrapper">
    <section
      v-for="yearToShow in yearsToShow"
      :key="yearToShow"
      :ref="(element) => observeYear(element)"
      :data-year="yearToShow">
      <h4>VIZBI {{ yearToShow }}</h4>
      <div class="photosInOneYear" :aria-busy="!getIsShown(yearToShow)">
        <template v-if="getIsShown(yearToShow)">
          <Photo
            v-for="photo in photos.byYear[yearToShow]"
            :key="photo.id"
            :photo="photo"
            :year="yearToShow"
            @highlight="updateHighlightedPhoto" />
        </template>
        <!-- placeholders, as many as the year's photos, until they're fetched -->
        <figure
          v-for="index in photos.counts[yearToShow] || 0"
          v-else
          :key="index"
          class="placeholder" />
      </div>
    </section>
  </div>
  <PhotoHighlightState :image="photos.byID[photos.highlight]" @hide="updateHighlightedPhoto" />
</template>

<script setup>
import NavigationPhotos from "@/components/Navigation/Photos";
import Photo from "@/components/Photo";
import PhotoHighlightState from "@/components/PhotoHighlightState";
import {reactive, ref, computed, onMounted, onBeforeUnmount} from "vue";
import {useRoute} from "vue-router";
import {fetchFlickrSummary, fetchFlickrYear} from "@/composables/useFlickrSearch";

// as on YouTube (2026-10-16): every year laid out at once with placeholders, the newest two years
// fetched straight away, and each further year fetched as it comes near the screen. A year's
// photos are shown only once it's near the screen (fetched early or not), so the page never
// builds hundreds of photos no one has scrolled to
const FIRST_YEARS = 2;
const AHEAD = "1500px 0px"; // fetch a year this far before it's scrolled into view

const route = useRoute();
const photos = reactive({byID: {}, byYear: {}, counts: {}, years: [], total: 0, highlight: ""});
const year = ref(route.params.photoYear || "");
const fetching = new Set();
const nearYears = reactive(new Set()); // years whose section has come near the screen
const yearsToShow = computed(() => (year.value ? [year.value] : photos.years));

async function loadYear(yearToLoad) {
  if (!yearToLoad || photos.byYear[yearToLoad] || fetching.has(yearToLoad)) return;
  fetching.add(yearToLoad);
  const data = await fetchFlickrYear(yearToLoad);
  fetching.delete(yearToLoad);
  if (!data) return;
  Object.assign(photos.byID, data.byID);
  photos.byYear[yearToLoad] = data.byYear[yearToLoad] || [];
}

const getIsShown = (yearToShow) => nearYears.has(yearToShow) && Boolean(photos.byYear[yearToShow]);

function onNear(entries) {
  for (const entry of entries.filter((item) => item.isIntersecting)) {
    nearYears.add(entry.target.dataset.year);
    loadYear(entry.target.dataset.year);
  }
}

const observer = new IntersectionObserver(onNear, {rootMargin: AHEAD});

function observeYear(element) {
  if (element) observer.observe(element);
}

async function loadSummary() {
  const summary = await fetchFlickrSummary();
  if (!summary) return;
  photos.years = summary.years.map((entry) => entry.year);
  photos.counts = Object.fromEntries(summary.years.map((entry) => [entry.year, entry.count]));
  photos.total = summary.total;
}

// the newest years as of the calendar, alongside the summary (no wait for it); the summary then
// names the newest years that have photos, in case those differ (e.g. early in a new year)
function getLikelyFirstYears() {
  const thisYear = new Date().getFullYear();
  return Array.from({length: FIRST_YEARS}, (_, index) => String(thisYear - index));
}

onMounted(async () => {
  const early = year.value ? [year.value] : getLikelyFirstYears();
  const loads = early.map(loadYear);
  await loadSummary();
  if (!year.value) loads.push(...photos.years.slice(0, FIRST_YEARS).map(loadYear));
  await Promise.all(loads);
});

onBeforeUnmount(() => observer.disconnect());

const setYear = (yearToSet) => {
  year.value = yearToSet;
  if (yearToSet) nearYears.add(yearToSet);
  loadYear(yearToSet);
};

const updateHighlightedPhoto = (photoToHighlight) => {
  photos.highlight = photoToHighlight?.id || "";
};
</script>

<style scoped>
aside {
  font-weight: 200;
  font-size: 0.9rem;
  margin-top: 8px;
  margin-bottom: 0px;
}
/* a placeholder takes a photo's place in the row (Photo.vue's figure), grey until it's fetched */
figure.placeholder {
  flex-grow: 1;
  width: calc(33% - 1rem);
  height: 12.5rem;
  margin: 0;
  background: #eee;
}
/* not :global(.narrow): in a scoped style, Vue drops what follows :global(), which made this
   ".narrow {width: …}" for every page, the whole site half the width of a phone (2026-10-21) */
.narrow figure.placeholder,
.medium figure.placeholder {
  width: calc(50% - 1rem);
}
.photosInOneYear {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 5px 5px;
  list-style: none;
  margin: 0;
  padding: 0;
}
</style>
